import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ViewChildren,
  QueryList,
  Inject,
  Output,
  EventEmitter,
  OnChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  AddressViewModel,
  AliasFieldDefinition,
  CombinedFilingData,
  ContactAliasFormGroup,
  ContactAliasViewModel,
  ContactFormGroup,
  ContactOrganizationFormGroup,
  ContactOrganizationViewModel,
  ContactPersonFormGroup,
  ContactProfile,
  ContactSpec,
  ContactType,
  ContactViewModel,
  EmailAddressSpec,
  EmailAddressViewModel,
  FilingProfile,
  FsxContactApiService,
  FsxFilingApiService,
  IContactApiService,
  IdentificationCommonCategory,
  IdentificationSpec,
  IdentificationViewModel,
  IFilingApiService,
  OrganizationSpec,
  ParticipantCategory,
  ParticipantFormMode,
  ParticipantSpec,
  PersonalNameViewModel,
  PersonNameSpec,
  PhoneSpec,
  PhoneViewModel,
  RequestContactOrganizationViewModel,
  RequestParticipantAliasViewModel,
  RequestParticipantRepresentationViewModel,
  RequestParticipantViewModel,
  TextFieldDefinition,
} from '@fsx/fsx-shared';
import {
  AddressComponentFieldDefinition,
  AddressFormGroup,
  EmailFormGroup,
  FormArrayWithModel,
  FormControlWithModel,
  FsxAddressComponent,
  FsxAliasComponent,
  FsxEmailComponent,
  FsxIdentificationComponent,
  FsxPersonNameComponent,
  FsxPhoneComponent,
  FsxTextComponent,
  IdentificationFormGroup,
  PersonNameFormGroup,
  PhoneFormGroup,
  SidePanelFooterConfig,
  SidePanelHeaderConfig,
} from '@fsx/ui-components';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import {
  FsxEditParticipantOrchestrationService,
  IEditParticipantOrchestrationService,
} from '../../parties/orchestration-services/edit-participant-orchestration.service';
import { FsxReferenceResolver } from '../../shared/resolvers/list-reference.resolver';
import {
  ContactFormService,
  FsxContactFormService,
  IContactFormService,
} from '../contact-form/contact-form.service';
import {
  FsxValidatePartiesOrchestrationService,
  IValidatePartiesOrchestrationService,
} from '../../filing-editor/services/orchestration/validate-parties-orchestration.service';
import {
  FsxPanelService,
  IPanelService,
} from '../../shared/services/panel.service';
import {
  FsxContactsListService,
  IContactsListService,
} from '../contacts-list/contacts-list.service';

/**
 * The config object for the participant form component.
 */
export interface ParticipantFormConfig {
  formMode: ParticipantFormMode;

  /**
   * The participant category of the participant being edited. Used to look
   * up the participant spec when in edit mode.
   */
  participantCategory: ParticipantCategory;

  /**
   * The flag to pass to the participant form so that the form can be used to
   * add/edit representation participants.
   */
  isRepresentation: boolean;

  /**
   * The participant to edit in the participant form.
   */
  participant: RequestParticipantViewModel;
}

const ShowAllOptions = {
  YES: true,
  NO: false,
};
@Component({
  selector: 'fsx-participant-form',
  templateUrl: 'participant-form.component.html',
  styleUrls: ['./participant-form.component.scss'],
  providers: [
    {
      provide: FsxContactFormService,
      useClass: ContactFormService,
    },
  ],
})
export class ParticipantFormComponent
  implements OnChanges, OnDestroy, AfterViewInit
{
  @Input() participantFormGroup!: FormGroup<ContactFormGroup>;
  @Input() formMode!: ParticipantFormMode;
  @Input() combinedFilingData!: CombinedFilingData;
  @Input() participant!: RequestParticipantViewModel | null;
  @Input() representation!: RequestParticipantRepresentationViewModel;
  @Input() participantCategory!: ParticipantCategory;
  @Input() isRepresentation!: boolean;
  @Output() isFormValidEvent = new EventEmitter<boolean>();

  @ViewChild('personNameField') personNameField!: FsxPersonNameComponent;
  @ViewChild('organizationField') organizationField!: FsxTextComponent;
  @ViewChildren('barNumberField')
  barNumberFields!: QueryList<FsxIdentificationComponent>;
  @ViewChildren('addressField') addressFields!: QueryList<FsxAddressComponent>;
  @ViewChildren('phoneField') phoneFields!: QueryList<FsxPhoneComponent>;
  @ViewChildren('emailField') emailFields!: QueryList<FsxEmailComponent>;
  @ViewChildren('identificationField')
  identificationFields!: QueryList<FsxIdentificationComponent>;
  @ViewChildren('aliasField') aliasFields!: QueryList<FsxAliasComponent>;

  public headerConfig!: SidePanelHeaderConfig;
  public footerConfig!: SidePanelFooterConfig;
  public ContactType = ContactType;
  public contactType: ContactType = ContactType.Person;
  public participantFormMode = ParticipantFormMode;
  public contactProfile!: ContactProfile;
  public showProgressBar = false;
  public participantSpec!: ParticipantSpec | undefined;

  public resolver!: FsxReferenceResolver;
  public personNameFieldDefinition!: PersonNameSpec;
  public personNameInitialValues!: PersonalNameViewModel;
  public organizationFieldDefinition!: OrganizationSpec;
  public organizationInitialValues!:
    | ContactOrganizationViewModel
    | RequestContactOrganizationViewModel;
  public addressFieldDefinition!: AddressComponentFieldDefinition;
  public addressInitialValues: AddressViewModel[] = [];
  public phoneFieldDefinition!: PhoneSpec;
  public phoneInitialValues: PhoneViewModel[] = [];
  public barIdentificationInitialValues: IdentificationViewModel[] = [];
  public otherIdentificationInitialValues: IdentificationViewModel[] = [];
  public identificationFieldDefinition!: IdentificationSpec;
  public identificationFormArray!: FormArrayWithModel<
    FormGroup<IdentificationFormGroup>
  >;
  public showBarIdentificationComponent = false;
  public barFormArray!: FormArrayWithModel<FormGroup<IdentificationFormGroup>>;
  public showOtherIdentificationComponent = false;
  public otherIdentificationFormArray!: FormArrayWithModel<
    FormGroup<IdentificationFormGroup>
  >;
  public emailFieldDefinition!: EmailAddressSpec;
  public emailInitialValues: EmailAddressViewModel[] = [];
  public aliasFieldDefinition!: AliasFieldDefinition;
  public aliasInitialValues:
    | RequestParticipantAliasViewModel[]
    | ContactAliasViewModel[] = [];
  public contact!: ContactViewModel | null;
  public idLength!: number;

  private destroy$: Subject<unknown> = new Subject();

  constructor(
    @Inject(FsxContactApiService)
    private readonly contactApiService: IContactApiService,
    @Inject(FsxFilingApiService)
    private readonly filingApiService: IFilingApiService,
    @Inject(FsxEditParticipantOrchestrationService)
    readonly editParticipantOrchestrationService: IEditParticipantOrchestrationService,
    @Inject(FsxContactFormService)
    readonly contactFormService: IContactFormService,
    @Inject(FsxPanelService) public readonly panelService: IPanelService,
    @Inject(FsxContactsListService)
    readonly contactsListService: IContactsListService,
    @Inject(FsxValidatePartiesOrchestrationService)
    readonly validatePartiesOrchestrationService: IValidatePartiesOrchestrationService
  ) {}

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler() {
    if (!this.headerConfig && this.panelService.dialogsArray.length === 1) {
      this.closeFormDialog();
    }
  }

  @HostListener('document:keydown.enter', ['$event']) onEnterKeyHandler(
    $event: KeyboardEvent
  ) {
    // don't action the return key if the form isn't valid
    if ($event.keyCode === 13 && this.participantFormGroup.invalid) {
      return;
    }
    this.footerConfig.buttons[1].action();
  }

  ngOnChanges(): void {
    if (this.combinedFilingData) {
      if (
        this.formMode === ParticipantFormMode.EditParticipant ||
        this.formMode === ParticipantFormMode.AddParticipant
      ) {
        this.setParticipantForm();
      } else if (this.formMode === ParticipantFormMode.EditRepresentation) {
        this.setRepresentationForm();
      }
    }
  }

  ngAfterViewInit(): void {
    if (
      this.formMode === ParticipantFormMode.EditParticipant ||
      this.formMode === ParticipantFormMode.EditRepresentation
    ) {
      // ToDo: Disabling this for now as it is causing issues with the intended behavior of the form. The side effect here
      // ToDo: is that invalid parties with pre-filled data with invalid fields will not have their errors surfaced until the user
      // ToDo: touches them. It's also important to note that concierge team will not be concerned about filling out the form,
      // ToDo: which is why this should be disabled for now until a solution can be found, since leaving it enabled will be an annoyance for them.
      this.validate();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public setActiveForm(contactType: ContactType): void {
    this.contactType = contactType;
    this.participantFormGroup.controls.contactType.setValue(this.contactType);
  }

  private _setFormValidityListener(forceValidation: boolean): void {
    combineLatest([
      this.participantFormGroup.valueChanges,
      this.participantFormGroup.statusChanges,
    ])
      .pipe(
        debounceTime(500),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        tap(() => {
          const validForm = this.contactFormService.validateFormGroup(
            this.participantFormGroup,
            forceValidation
          );

          const isValid = validForm && this.participantFormGroup.valid;
          this.isFormValidEvent.emit(isValid);

          // this.validate();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  public closeFormDialog(): void {
    this.panelService.closeCurrentDialog();
  }

  private setParticipantForm(): void {
    if (this.combinedFilingData.filingProfile && this.participantCategory) {
      this.participantSpec = this.combinedFilingData.modeSpec?.participant.find(
        (spec) => {
          return (
            spec.participantCategory.name === this.participantCategory.name
          );
        }
      );

      this.resolver = new FsxReferenceResolver(
        this.combinedFilingData.filingProfile,
        {
          filingApi: this.filingApiService,
          filingId: this.combinedFilingData.filingProfile.id,
        }
      );

      this._setComponentsData(this.participant);
      this._setParticipantContactType(this.participant);
      this._setComponentsSpecs(
        this.participantSpec,
        this.combinedFilingData.filingProfile
      );

      // Important. The forceValidation parameter must be set to
      // false to prevent the form from being immediately invalid.
      this._setFormValidityListener(false);
    }
  }

  // Customize here
  private setRepresentationForm(): void {
    if (
      this.combinedFilingData.filingProfile &&
      this.participant &&
      this.participantCategory
    ) {
      this.formMode = ParticipantFormMode.EditRepresentation;
      this.contactType = ContactType.Person;
      this.participantSpec = this.combinedFilingData.modeSpec?.participant.find(
        (spec) => {
          return (
            spec.participantCategory.name === this.participantCategory.name
          );
        }
      );
      this.resolver = new FsxReferenceResolver(
        this.combinedFilingData.filingProfile,
        {
          filingApi: this.filingApiService,
          filingId: this.combinedFilingData.filingProfile.id,
        }
      );

      this._setComponentsData(this.participant);
      this._setParticipantContactType(this.participant);
      this._setComponentsSpecs(
        this.participantSpec,
        this.combinedFilingData.filingProfile
      );
      this._setFormValidityListener(true);
    }
  }

  public setPersonNameFormGroup(
    formGroup: FormGroup<PersonNameFormGroup>
  ): void {
    const personNameForm = new FormGroup<ContactPersonFormGroup>({
      personalName: formGroup,
    });
    this.participantFormGroup.setControl('person', personNameForm);
  }

  public setOrganizationFormControl(
    formControl: FormControlWithModel<TextFieldDefinition>
  ): void {
    const organizationForm = new FormGroup<ContactOrganizationFormGroup>({
      title: formControl,
    });
    this.participantFormGroup.setControl('organization', organizationForm);
  }

  public setAddressFromArray(
    addressFormArray: FormArrayWithModel<FormGroup<AddressFormGroup>>
  ): void {
    this.participantFormGroup.setControl('addresses', addressFormArray);
  }

  public setPhoneFormArray(
    setPhoneFormArray: FormArrayWithModel<FormGroup<PhoneFormGroup>>
  ): void {
    this.participantFormGroup.setControl('phones', setPhoneFormArray);
  }

  public setEmailFormArray(
    emailFormArray: FormArrayWithModel<FormGroup<EmailFormGroup>>
  ): void {
    this.participantFormGroup.setControl('emails', emailFormArray);
  }

  public setBarFormArray(
    identificationFormArray: FormArrayWithModel<
      FormGroup<IdentificationFormGroup>
    >
  ): void {
    this.participantFormGroup.setControl(
      'barIdentifications',
      identificationFormArray
    );
    this.barFormArray = identificationFormArray;
    this.barFormArray.valueChanges
      .pipe(
        debounceTime(500),
        tap(() => {
          this.idLength = this.getIdentificationsCount(
            this.barFormArray,
            this.otherIdentificationFormArray
          );
        })
      )
      .subscribe();
  }

  public setOtherIdFormArray(
    identificationFormArray: FormArrayWithModel<
      FormGroup<IdentificationFormGroup>
    >
  ): void {
    this.participantFormGroup.setControl(
      'otherIdentifications',
      identificationFormArray
    );
    this.otherIdentificationFormArray = identificationFormArray;
    this.otherIdentificationFormArray.valueChanges
      .pipe(
        debounceTime(500),
        tap(() => {
          this.idLength = this.getIdentificationsCount(
            this.barFormArray,
            this.otherIdentificationFormArray
          );
        })
      )
      .subscribe();
  }

  public setAliasFromArray(
    formArray: FormArrayWithModel<FormGroup<ContactAliasFormGroup>>
  ): void {
    this.participantFormGroup.setControl('aliases', formArray);
  }

  public validate(): void {
    // TODO - this needs to be cleverer, taking into account the specs for
    // each child-component. For example, if emails has a minRequired of 0, it's OK
    // to have an empty email (event though the email address field itself is required)

    if (this.personNameField) {
      this.personNameField.validate();
    }

    if (this.organizationField) {
      this.organizationField.validate();
    }

    if (this.barNumberFields && this.barNumberFields.length > 0) {
      this.barNumberFields.forEach((fld: FsxIdentificationComponent) => {
        return fld.validate();
      });
    }

    if (this.addressFields && this.addressFields.length > 0) {
      this.addressFields.forEach((fld: FsxAddressComponent) => {
        fld.validate();
      });
    }

    if (this.phoneFields && this.phoneFields.length > 0) {
      this.phoneFields.forEach((fld: FsxPhoneComponent) => {
        fld.validate();
      });
    }

    if (this.emailFields && this.emailFields.length > 0) {
      this.emailFields.forEach((fld: FsxEmailComponent) => {
        fld.validate();
      });
    }

    if (this.identificationFields && this.identificationFields.length > 0) {
      this.identificationFields.forEach((fld: FsxIdentificationComponent) => {
        fld.validate();
      });
    }

    if (this.aliasFields && this.aliasFields.length > 0) {
      this.aliasFields.forEach((fld: FsxAliasComponent) => {
        fld.validate();
      });
    }
  }

  private getIdentificationsCount(
    barFormArray: FormArrayWithModel<FormGroup<IdentificationFormGroup>>,
    otherIdentificationFormArray: FormArrayWithModel<
      FormGroup<IdentificationFormGroup>
    >
  ) {
    const barControlsCount = barFormArray?.controls?.length ?? 0;
    const otherControlsCount =
      otherIdentificationFormArray?.controls?.length ?? 0;

    return barControlsCount + otherControlsCount;
  }

  private _setComponentsSpecs(
    spec: ParticipantSpec | ContactSpec | undefined,
    profile: ContactProfile | FilingProfile
  ): void {
    if (spec?.person?.personalName) {
      this.personNameFieldDefinition = spec.person.personalName;
    }

    if (spec?.organization) {
      this.organizationFieldDefinition = spec.organization;
    }

    if (spec?.address) {
      const profileName =
        spec.address.addressProfileName ?? profile.defaultAddressProfileName;

      const addressCategoriesDefinition = profile.addressProfiles.find(
        (prof) => prof.name === profileName
      );

      if (addressCategoriesDefinition) {
        this.addressFieldDefinition = {
          ...spec.address,
          ...addressCategoriesDefinition.spec,
        };
      }
    }

    if (spec?.phone) {
      this.phoneFieldDefinition = spec.phone;
    }

    if (spec?.email) {
      this.emailFieldDefinition = spec.email;
    }

    if (spec?.identification) {
      this.identificationFieldDefinition = spec.identification;
      if (spec.identification.category?.listReference) {
        const idCategories =
          this.resolver.getIdentificationCategoryDropdownOptions(
            spec.identification.category?.listReference,
            ShowAllOptions.YES
          );
        if (
          idCategories.filter((option) => {
            return (
              option.category?.commonCategory !=
              IdentificationCommonCategory.BarNumber
            );
          }).length > 0
        ) {
          this.showOtherIdentificationComponent = true;
        }
        if (
          idCategories.filter((option) => {
            return option.category?.commonCategory == 'BarNumber';
          }).length > 0
        ) {
          this.showBarIdentificationComponent = true;
        }
      }
    }

    if (spec?.alias) {
      this.aliasFieldDefinition = spec.alias;
    }
  }

  private _setParticipantContactType(
    participant: RequestParticipantViewModel | null
  ): void {
    // if (participant?.contactType) {
    //   this.contactType = participant?.contactType;
    //   this.participantFormGroup.controls.contactType.setValue(this.contactType);
    // }

    if (participant) {
      if (participant.contactType === ContactType.Unknown) {
        this.contactType = ContactType.Person;
      } else {
        this.contactType = participant.contactType;
      }
      this.participantFormGroup.controls.contactType.setValue(this.contactType);
    }
  }

  private _setComponentsData(
    participant: RequestParticipantViewModel | null
  ): void {
    if (participant?.person?.personalName) {
      this.personNameInitialValues = participant?.person?.personalName;
    }

    if (participant?.organization) {
      this.organizationInitialValues = participant.organization;
    }

    if (participant?.addresses) {
      this.addressInitialValues = participant.addresses;
    }

    if (participant?.emails) {
      this.emailInitialValues = participant.emails;
    }

    if (participant?.phones) {
      this.phoneInitialValues = participant.phones;
    }

    if (participant?.identifications) {
      this.barIdentificationInitialValues = participant.identifications.filter(
        (identification) => {
          return (
            identification.category.commonCategory ===
            IdentificationCommonCategory.BarNumber
          );
        }
      );
      this.otherIdentificationInitialValues =
        participant.identifications.filter((identification) => {
          return (
            identification.category.commonCategory !==
            IdentificationCommonCategory.BarNumber
          );
        });
    }

    if (participant?.aliases) {
      this.aliasInitialValues = participant.aliases;
    }
  }
}
